import React, { useEffect, useState } from 'react';
import { collection, query, where, onSnapshot, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  Paper, Typography, Table, TableBody, TableCell, TableHead, TableRow, Button, Box,
  Menu, MenuItem, Alert,
} from '@mui/material';
import { db } from '../firebase';

// New adults who said "the adult 4 on 4 league" on /next-steps or their
// welcome page. A list to work by hand: invite them to a team or the
// free-agent sign-up, then mark them Done to take them off it — or put them
// straight onto a league's wait-list, which takes them off it too.
export default function AdultLeagueInterestList() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState('');
  const [leagues, setLeagues] = useState([]);
  const [menu, setMenu] = useState(null); // { anchor, row }
  const [msg, setMsg] = useState(null);   // { severity, text }

  useEffect(() => onSnapshot(
    query(collection(db, 'adultInterests'), where('league', '==', true)),
    snap => setRows(snap.docs.map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (b.leagueAddedAt?.seconds || 0) - (a.leagueAddedAt?.seconds || 0))),
    () => setRows([]),
  ), []);

  // Leagues still taking sign-ups are the only ones worth waiting for.
  useEffect(() => {
    getDocs(query(collection(db, 'leagues'), where('status', '==', 'registration')))
      .then(snap => setLeagues(snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => String(a.startDate || '').localeCompare(String(b.startDate || '')))))
      .catch(() => setLeagues([]));
  }, []);

  if (!rows || rows.length === 0) return null;

  const done = async (uid) => {
    setBusy(uid);
    try { await httpsCallable(getFunctions(), 'clearAdultLeagueInterest')({ uid }); } catch (e) { /* the row stays */ }
    setBusy('');
  };

  const waitlist = async (row, league) => {
    setMenu(null); setBusy(row.id); setMsg(null);
    try {
      const { data } = await httpsCallable(getFunctions(), 'waitlistAdultInterest')({ uid: row.id, leagueId: league.id });
      setMsg({ severity: 'success', text: `${row.name} is #${data.waitlistPosition} on the ${league.name} wait-list.` });
    } catch (e) {
      setMsg({ severity: 'error', text: e.message });
    }
    setBusy('');
  };

  return (
    <Paper elevation={2} sx={{ borderRadius: 2, p: 2, mb: 3, border: '1px solid #C9A84C' }}>
      <Box sx={{ mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Adult league interest ({rows.length})
        </Typography>
        <Typography variant="caption" color="text.secondary">
          New adults who asked about the 4 on 4 league. Put them on a league's wait-list, or invite them and mark them Done.
        </Typography>
      </Box>
      {msg && <Alert severity={msg.severity} onClose={() => setMsg(null)} sx={{ mb: 1 }}>{msg.text}</Alert>}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell><strong>Name</strong></TableCell>
            <TableCell><strong>Phone</strong></TableCell>
            <TableCell><strong>Email</strong></TableCell>
            <TableCell><strong>Asked</strong></TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(r => (
            <TableRow key={r.id}>
              <TableCell>{r.name}</TableCell>
              <TableCell>{r.phone ? <a href={`tel:${r.phone}`}>{r.phone}</a> : '—'}</TableCell>
              <TableCell>{r.email ? <a href={`mailto:${r.email}`}>{r.email}</a> : '—'}</TableCell>
              <TableCell>{r.leagueAddedAt?.toDate ? r.leagueAddedAt.toDate().toLocaleDateString() : '—'}</TableCell>
              <TableCell align="right">
                <Button size="small" variant="outlined" disabled={busy === r.id || leagues.length === 0}
                  onClick={e => setMenu({ anchor: e.currentTarget, row: r })} sx={{ mr: 1 }}>
                  Wait-list
                </Button>
                <Button size="small" disabled={busy === r.id} onClick={() => done(r.id)}>Done</Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Menu anchorEl={menu?.anchor || null} open={!!menu} onClose={() => setMenu(null)}>
        {leagues.map(l => (
          <MenuItem key={l.id} onClick={() => waitlist(menu.row, l)}>{l.name}</MenuItem>
        ))}
      </Menu>
    </Paper>
  );
}
